import React, { createContext, useContext, useEffect, useState } from "react";
import { CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import userPool from "../../util/cognito/userPool";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import reqProvider from "../../util/requests/reqProvider";
import { AxiosResponse } from "axios";
import refreshToken from "../../util/cognito/refreshToken";

interface AuthContextType {
  user: CognitoUser | null;
  token: string | null;
  permissions: string[];
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  refreshToken: () => void;
  loginWithRedirect: (username: string, password: string, redirect: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [permissions, setPermissions] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchData('auth/me')
    }
  }, [token]);

  const signIn = (username: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: username, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: username, Password: password });

      user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idToken = session.getIdToken().getJwtToken();
          localStorage.setItem("token", idToken);
          setToken(idToken);
          setUser(user);
          resolve();
        },
        onFailure: (err) => reject(err),
      });
    });
  };

  const handleRefreshToken = async () => {
    try {
      const newToken = await refreshToken();
      localStorage.setItem("token", newToken);
      setToken(newToken);
    } catch (error) {
      console.error("Failed to refresh token", error);
    }
  };

  const signOut = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    localStorage.removeItem("token");
    setUser(null);
    setToken(null);
    setPermissions([]);
  };

  const encryptData = (data: object): string => {
    const SECRET_KEY: string | undefined = process.env.REACT_APP_ENCRYPTION_KEY;
    const jsonString = JSON.stringify(data);
    if (!SECRET_KEY) {
      throw new Error("Environment variable REACT_APP_ENCRYPTION_KEY is not defined");
    }
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
    return encodeURIComponent(encrypted);
  };

  const fetchData = async (url: string): Promise<AxiosResponse | undefined> => {
    try {
      const response = await reqProvider.get(url);
      setPermissions(response.data)
      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const loginWithRedirect = async (userName: string, password: string, redirect: string) => {
    try {
      await signIn(userName, password);
      const response: AxiosResponse | undefined = await fetchData('auth/me');
      if (response?.data.companies.length < 1) {
        navigate('/companyRegistration');
      } else {
        // const encryptedData = encryptData({ userName, password, redirect });
        // const baseUrl = process.env.REACT_APP_OLD_URL as string;
        // const hash = '#/login';
        // const url = new URL(baseUrl);
        // window.location.href = `${url.origin}${url.pathname}${hash}?data=${encryptedData}`;
        navigate('/groups')
      }
    } catch (error) {
      console.error('Error during login with redirect:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, permissions, signIn, signOut, refreshToken: handleRefreshToken, loginWithRedirect }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};